import type { DriveStep } from 'driver.js';

export type PracticeSessionWalkthroughFlags = {
  mode: 'verbal' | 'text';
  showInvitePanel: boolean;
  showSessionToolbar: boolean;
  /** Text: header End session. Video call: hang-up in the control bar. */
  showEndSession: boolean;
  showTextComposer: boolean;
};

function el(selector: string): Element {
  return (document.querySelector(selector) as Element) ?? document.body;
}

/**
 * Pure step list for the in-session walkthrough. Element resolvers run when each step is shown.
 */
export function buildPracticeSessionWalkthroughSteps(
  flags: PracticeSessionWalkthroughFlags,
): DriveStep[] {
  const { mode, showInvitePanel, showSessionToolbar, showEndSession, showTextComposer } = flags;

  const steps: DriveStep[] = [
    {
      element: () => el('[data-tour="ps-intro"]'),
      popover: {
        title: 'During your session',
        description:
          'A quick map of timers, sharing, invites, and how to speak or type. Replay this walkthrough from Profile whenever you like.',
        side: 'bottom',
        align: 'start',
      },
    },
  ];

  if (showInvitePanel) {
    steps.push({
      element: () => el('[data-tour="ps-invite-panel"]'),
      popover: {
        title: 'Email invites',
        description:
          'When you are the host on an org-open session, invite teammates by email with a calendar attachment.',
        side: 'bottom',
        align: 'start',
      },
    });
  }

  if (showSessionToolbar) {
    steps.push({
      element: () => el('[data-tour="ps-toolbar"]'),
      popover: {
        title: 'Teammate access and link',
        description:
          'Control whether the session is private or open to your org, and copy a shareable link when multi-user practice is enabled.',
        side: 'bottom',
        align: 'start',
      },
    });
  }

  if (mode === 'text') {
    steps.push({
      element: () => el('[data-tour="ps-text-header-actions"]'),
      popover: {
        title: 'Header controls',
        description:
          'Time remaining, end session, transcript panel, and back to the session list. End session starts evaluation when you are the host.',
        side: 'bottom',
        align: 'end',
      },
    });
    if (showTextComposer) {
      steps.push({
        element: () => el('[data-tour="ps-text-composer"]'),
        popover: {
          title: 'Send messages',
          description:
            'Type your pitch or replies here and press Send. The transcript column shows the full thread.',
          side: 'top',
          align: 'start',
        },
      });
    }
  } else {
    steps.push({
      element: () => el('[data-tour="ps-verbal-top-meta"]'),
      popover: {
        title: 'Timer and context',
        description:
          'Countdown to the time limit, participant count, and quick context chips. Video call sessions use your microphone (and optional camera) in the control bar below.',
        side: 'bottom',
        align: 'start',
      },
    });
    steps.push({
      element: () => el('[data-tour="ps-verbal-controls"]'),
      popover: {
        title: 'Mic, camera, and call controls',
        description:
          'Mute or unmute, turn camera on or off, open device settings, toggle the transcript, and hang up to end. In push-to-talk mode, press and hold the mic (or Space) to record each turn.',
        side: 'top',
        align: 'center',
      },
    });
    if (showEndSession) {
      steps.push({
        element: () => el('[data-tour="ps-verbal-end"]'),
        popover: {
          title: 'End the call',
          description:
            'Hang up when you are finished. We will generate feedback when the session ends.',
          side: 'top',
          align: 'center',
        },
      });
    }
  }

  return steps;
}
